/**
 * MATERIAL PREVISTO (FASE 9.6-B1)
 *
 * · Plan: «Materiales previstos» de una tarea (modal de tarea del editor). Sólo
 *   se edita en BORRADOR; una versión publicada se ve en solo lectura.
 * · OT: por línea, previsto vs real (consumos de 9.6-A) y desviación en unidad
 *   de stock. Una sustitución (previsto A, consumido B) se muestra como tal,
 *   nunca restando artículos distintos.
 *
 * El previsto NO mueve ni reserva stock: «Stock disponible inferior al
 * material previsto» es un aviso, planificar sigue permitido. Sin lote, sin
 * almacén y sin series: eso lo concreta el consumo real.
 */
import { useState, type ReactNode } from 'react';

import {
  api,
  type MaterialPlan,
  type MaterialesTareaPlan,
  type NoPrevistoOt,
  type OpcionArticuloStock,
  type PrevistoOt,
  type UnidadArticuloPrevisto,
} from '../api.ts';
import { Error as ErrorCaja, fechaDeInstante, Modal, Panel, useDatos } from '../componentes/base.tsx';
import { Buscador } from '../componentes/buscador.tsx';
import { buscarArticulos, erroresDe, ErrorStock, nombreArticulo } from './stock.tsx';

export const TEXTO_STOCK_INFERIOR = 'Stock disponible inferior al material previsto.';
export const TEXTO_PREVISTO = 'El material previsto no descuenta ni reserva stock: es lo que se espera usar.';

const INCIDENCIA: Record<string, string> = {
  ARTICULO_INACTIVO: 'Artículo inactivo',
  CONVERSION_INACTIVA: 'Conversión de unidad desactivada',
  CONVERSION_NO_DISPONIBLE: 'Conversión de unidad no disponible',
};

/** «+0.15 L» / «−0.2 L» / «0 L». */
export function desviacionTexto(d: string, unidad: string): string {
  const n = Number(d);
  if (n === 0) return `0 ${unidad}`;
  return `${n > 0 ? '+' : '−'}${String(Math.abs(n)).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')} ${unidad}`;
}

// ============================================================ formulario común

interface DatosMaterial { idArticulo: string; cantidad: string; idUnidad: string; instrucciones: string }

/** Artículo (buscador de inventario), cantidad, unidad (stock o conversión activa) e instrucciones opcionales. */
function CamposMaterial({ articulo, setArticulo, cantidad, setCantidad, idUnidad, setIdUnidad, instrucciones, setInstrucciones, error, fijarArticulo, prefijo }: {
  articulo: OpcionArticuloStock | null; setArticulo: (a: OpcionArticuloStock | null) => void;
  cantidad: string; setCantidad: (v: string) => void; idUnidad: string; setIdUnidad: (v: string) => void;
  instrucciones: string; setInstrucciones: (v: string) => void; error: unknown;
  /** Edición: el artículo no cambia (para cambiarlo, sustituir). */
  fijarArticulo?: { codigo: string; descripcion: string; unidades: UnidadArticuloPrevisto[] };
  prefijo: string;
}): ReactNode {
  const campos = erroresDe(error);
  const unidades = fijarArticulo?.unidades ?? articulo?.unidades.map((u) => ({ ...u })) ?? [];
  const serializado = articulo?.trazabilidad?.controlaSeries === true;
  return (
    <div className="form-material">
      {fijarArticulo !== undefined ? (
        <p className="pequena"><strong>{fijarArticulo.descripcion}</strong> <span className="mono">{fijarArticulo.codigo}</span></p>
      ) : (
        <Buscador<OpcionArticuloStock> etiqueta="Artículo *" valor={articulo} buscar={buscarArticulos(null)} clave={(a) => a.id} texto={nombreArticulo}
          detalle={(a) => `Unidad ${a.unidadStock.codigo}${a.trazabilidad?.controlaSeries === true ? ' · con nº de serie (no admitido)' : ''}`}
          alElegir={(a) => { setArticulo(a); setIdUnidad(a?.unidadStock.id ?? ''); }} error={campos['idArticulo']}
          sinResultados="Ningún artículo activo con ese texto." />
      )}
      {serializado && <p className="nota error pequena" role="alert">Este artículo se controla por número de serie: no se admite como material de Limpieza.</p>}
      {(fijarArticulo !== undefined || (articulo !== null && !serializado)) && (
        <div className="form-rejilla">
          <div className="campo"><label htmlFor={`${prefijo}-cant`}>Cantidad *</label>
            <input id={`${prefijo}-cant`} inputMode="decimal" value={cantidad} onChange={(ev) => setCantidad(ev.target.value)} />
            {campos['cantidad'] !== undefined && <div className="error-campo">{campos['cantidad']}</div>}</div>
          <div className="campo"><label htmlFor={`${prefijo}-ud`}>Unidad *</label>
            <select id={`${prefijo}-ud`} value={idUnidad} onChange={(ev) => setIdUnidad(ev.target.value)}>
              {unidades.map((u) => <option key={u.id} value={u.id}>{u.codigo}{u.factor === '1' ? '' : ` (= ${u.factor} ${unidades[0]?.codigo ?? ''})`}</option>)}
            </select>
            {campos['idUnidad'] !== undefined && <div className="error-campo">{campos['idUnidad']}</div>}</div>
        </div>
      )}
      <div className="campo"><label htmlFor={`${prefijo}-ins`}>Instrucciones (opcional)</label>
        <input id={`${prefijo}-ins`} maxLength={500} value={instrucciones} onChange={(ev) => setInstrucciones(ev.target.value)} placeholder="p. ej. diluir 1:10" />
        {campos['instrucciones'] !== undefined && <div className="error-campo">{campos['instrucciones']}</div>}</div>
    </div>
  );
}

function useAccion(alTerminar: () => void): { error: unknown; ocupado: boolean; ejecutar: (f: () => Promise<unknown>) => Promise<boolean>; limpiar: () => void } {
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  return {
    error, ocupado, limpiar: () => setError(null),
    ejecutar: async (f) => {
      setError(null);
      setOcupado(true);
      try { await f(); alTerminar(); return true; } catch (e) { setError(e); return false; } finally { setOcupado(false); }
    },
  };
}

// ==================================================================== PLAN

/** «Materiales previstos» de una tarea de plan (modal de tarea). */
export function MaterialesTareaPlanSeccion({ idPlan, idLinea }: { idPlan: string; idLinea: string }): ReactNode {
  const base = `/api/planes/${idPlan}/lineas/${idLinea}/materiales`;
  const datos = useDatos(() => api.get<MaterialesTareaPlan>(base), [base]);
  const [dialogo, setDialogo] = useState<null | { t: 'nuevo' } | { t: 'editar'; m: MaterialPlan }>(null);
  const acc = useAccion(() => { setDialogo(null); datos.recargar(); });
  return (
    <section className="materiales-tarea" aria-label="Materiales previstos">
      <div className="fila" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h3 className="titulo-seccion">Materiales previstos</h3>
        {datos.datos?.editable === true && <button type="button" className="pequeno" onClick={() => { acc.limpiar(); setDialogo({ t: 'nuevo' }); }}>+ Añadir material</button>}
      </div>
      <Panel estado={datos}>
        {(d) => (
          <>
            {d.materiales.length === 0 ? <p className="suave pequena">Sin material previsto para esta tarea.</p> : (
              <ul className="lista-previstos" aria-label="Materiales de la tarea">
                {d.materiales.map((m) => (
                  <li key={m.id} className="fila-previsto">
                    <div><strong>{m.articulo.descripcion}</strong> <span className="mono pequena">{m.articulo.codigo}</span>
                      <div className="pequena">{m.cantidad} {m.unidad.codigo}{m.unidad.id !== m.articulo.unidadStock.id && m.cantidadStock !== null && <span className="suave"> (= {m.cantidadStock} {m.articulo.unidadStock.codigo})</span>}
                        {m.instrucciones !== null && <span className="suave"> · {m.instrucciones}</span>}</div>
                      {!m.articulo.activo && <span className="etiqueta error">Artículo inactivo</span>}
                      {m.stockInferior && <span className="etiqueta aviso" title={`Disponible en el centro: ${m.disponibleCentro} ${m.articulo.unidadStock.codigo}`}>{TEXTO_STOCK_INFERIOR}</span>}
                    </div>
                    {d.editable && (
                      <span className="fila acciones-previsto">
                        <button type="button" className="pequeno" disabled={!m.articulo.activo} onClick={() => { acc.limpiar(); setDialogo({ t: 'editar', m }); }}>Editar</button>
                        <button type="button" className="pequeno" disabled={acc.ocupado} onClick={() => void acc.ejecutar(() => api.del(`${base}/${m.id}`))}>Quitar</button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {!d.editable && d.plan.estado !== 'BORRADOR' && <p className="suave pequena">Versión publicada: su material es histórico. Para cambiarlo, crea una versión nueva.</p>}
            {acc.error !== null && dialogo === null && <ErrorStock error={acc.error} />}
            <p className="suave pequena">{TEXTO_PREVISTO}</p>
          </>
        )}
      </Panel>
      {dialogo !== null && (
        <DialogoMaterial titulo={dialogo.t === 'nuevo' ? 'Añadir material previsto' : 'Editar material previsto'} error={acc.error} ocupado={acc.ocupado}
          inicial={dialogo.t === 'editar' ? dialogo.m : null} cerrar={() => setDialogo(null)}
          enviar={(x) => void acc.ejecutar(() => dialogo.t === 'nuevo'
            ? api.post(base, { idArticulo: x.idArticulo, cantidad: x.cantidad, idUnidad: x.idUnidad, instrucciones: x.instrucciones })
            : api.patch(`${base}/${dialogo.m.id}`, { cantidad: x.cantidad, idUnidad: x.idUnidad, instrucciones: x.instrucciones }))} />
      )}
    </section>
  );
}

function DialogoMaterial({ titulo, inicial, error, ocupado, cerrar, enviar, observacion }: {
  titulo: string; inicial: { articulo: { codigo: string; descripcion: string; unidades: UnidadArticuloPrevisto[] }; cantidad: string; unidad: { id: string }; instrucciones: string | null } | null;
  error: unknown; ocupado: boolean; cerrar: () => void; enviar: (d: DatosMaterial & { observacion: string }) => void;
  /** Sustituir: además, observación de la sustitución. */
  observacion?: boolean;
}): ReactNode {
  const [articulo, setArticulo] = useState<OpcionArticuloStock | null>(null);
  const [cantidad, setCantidad] = useState(inicial?.cantidad ?? '');
  const [idUnidad, setIdUnidad] = useState(inicial?.unidad.id ?? '');
  const [instrucciones, setInstrucciones] = useState(inicial?.instrucciones ?? '');
  const [obs, setObs] = useState('');
  const campos = erroresDe(error);
  const listo = (inicial !== null || (articulo !== null && articulo.trazabilidad?.controlaSeries !== true)) && cantidad.trim() !== '';
  return (
    <Modal titulo={titulo} cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className="principal" disabled={!listo || ocupado}
          onClick={() => enviar({ idArticulo: articulo?.id ?? '', cantidad, idUnidad: idUnidad || (articulo?.unidadStock.id ?? ''), instrucciones, observacion: obs })}>Guardar</button></>}>
      {error !== null && Object.keys(campos).length === 0 && <ErrorStock error={error} />}
      <p className="nota info pequena" role="note">{TEXTO_PREVISTO}</p>
      <CamposMaterial articulo={articulo} setArticulo={setArticulo} cantidad={cantidad} setCantidad={setCantidad} idUnidad={idUnidad} setIdUnidad={setIdUnidad}
        instrucciones={instrucciones} setInstrucciones={setInstrucciones} error={error} prefijo="mp"
        {...(inicial !== null ? { fijarArticulo: inicial.articulo } : {})} />
      {observacion === true && (
        <div className="campo"><label htmlFor="mp-obs">Motivo de la sustitución (opcional)</label>
          <input id="mp-obs" maxLength={1000} value={obs} onChange={(ev) => setObs(ev.target.value)} /></div>
      )}
    </Modal>
  );
}

// ====================================================================== OT

/** Previsto vs real de UNA línea de OT. */
export function PrevistosLinea({ idOt, idLinea, previstos, noPrevistos, editable, alCambiar }: {
  idOt: string; idLinea: string; previstos: PrevistoOt[]; noPrevistos: NoPrevistoOt[]; editable: boolean; alCambiar: () => void;
}): ReactNode {
  const base = `/api/ots/${idOt}/previstos`;
  const [dialogo, setDialogo] = useState<null | { t: 'nuevo' } | { t: 'modificar' | 'sustituir' | 'retirar'; p: PrevistoOt }>(null);
  const [verHistorico, setVerHistorico] = useState(false);
  const acc = useAccion(() => { setDialogo(null); alCambiar(); });
  const activos = previstos.filter((p) => p.estado === 'ACTIVO');
  const historico = previstos.filter((p) => p.estado !== 'ACTIVO');
  if (activos.length === 0 && historico.length === 0 && noPrevistos.length === 0 && !editable) return null;
  return (
    <div className="previstos-linea" role="group" aria-label="Materiales previstos de la línea">
      <div className="fila" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h4 className="pequena titulo-previstos">Materiales previstos</h4>
        {editable && <button type="button" className="pequeno" onClick={() => { acc.limpiar(); setDialogo({ t: 'nuevo' }); }}>+ Añadir previsto</button>}
      </div>
      {activos.length === 0 ? <p className="suave pequena">Sin material previsto.</p> : (
        <ul className="lista-previstos" aria-label="Previsto vs real">
          {activos.map((p) => <FilaPrevisto key={p.id} p={p} editable={editable} accion={(t) => { acc.limpiar(); setDialogo({ t, p }); }} />)}
        </ul>
      )}
      {noPrevistos.length > 0 && (
        <ul className="lista-previstos" aria-label="Consumo no previsto">
          {noPrevistos.map((n) => (
            <li key={n.articulo.id} className="fila-previsto no-previsto">
              <div><strong>{n.articulo.descripcion}</strong> <span className="mono pequena">{n.articulo.codigo}</span> <span className="etiqueta neutra">No previsto</span>
                <div className="pequena">Real: {n.validadaStock} {n.unidadStock}{Number(n.pendienteStock) > 0 && <span className="suave"> · pendiente {n.pendienteStock} {n.unidadStock}</span>}</div></div>
            </li>
          ))}
        </ul>
      )}
      {historico.length > 0 && (
        <div className="pequena">
          <button type="button" className="enlace pequeno" aria-expanded={verHistorico} onClick={() => setVerHistorico((v) => !v)}>
            {verHistorico ? 'Ocultar' : 'Ver'} previstos retirados o sustituidos ({historico.length})</button>
          {verHistorico && (
            <ul className="lista-previstos historico" aria-label="Previstos retirados">
              {historico.map((p) => (
                <li key={p.id} className="fila-previsto retirado">
                  <div><span className="tachado">{p.articulo.descripcion} · {p.cantidad} {p.unidad.codigo}</span>{' '}
                    <span className="etiqueta neutra">{p.estado === 'SUSTITUIDO' ? `Sustituido por ${p.sustituidoPor?.descripcion ?? ''}` : 'Retirado'}</span>
                    <div className="suave">{p.retirada?.por} · {fechaDeInstante(p.retirada?.en ?? null)}{p.retirada?.observacion != null && ` · ${p.retirada.observacion}`}</div></div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {acc.error !== null && dialogo === null && <ErrorStock error={acc.error} />}
      {dialogo?.t === 'nuevo' && (
        <DialogoMaterial titulo="Añadir material previsto" inicial={null} error={acc.error} ocupado={acc.ocupado} cerrar={() => setDialogo(null)}
          enviar={(x) => void acc.ejecutar(() => api.post(`/api/ots/${idOt}/lineas/${idLinea}/previstos`,
            { idArticulo: x.idArticulo, cantidad: x.cantidad, idUnidad: x.idUnidad, instrucciones: x.instrucciones }))} />
      )}
      {dialogo?.t === 'modificar' && (
        <DialogoMaterial titulo="Modificar material previsto" inicial={dialogo.p} error={acc.error} ocupado={acc.ocupado} cerrar={() => setDialogo(null)}
          enviar={(x) => void acc.ejecutar(() => api.patch(`${base}/${dialogo.p.id}`,
            { cantidad: x.cantidad, idUnidad: x.idUnidad, instrucciones: x.instrucciones, versionEsperada: dialogo.p.version }))} />
      )}
      {dialogo?.t === 'sustituir' && (
        <DialogoMaterial titulo={`Sustituir ${dialogo.p.articulo.descripcion}`} inicial={null} observacion error={acc.error} ocupado={acc.ocupado} cerrar={() => setDialogo(null)}
          enviar={(x) => void acc.ejecutar(() => api.post(`${base}/${dialogo.p.id}/sustituir`,
            { idArticulo: x.idArticulo, cantidad: x.cantidad, idUnidad: x.idUnidad, instrucciones: x.instrucciones, observacion: x.observacion, versionEsperada: dialogo.p.version }))} />
      )}
      {dialogo?.t === 'retirar' && (
        <DialogoRetirar p={dialogo.p} error={acc.error} ocupado={acc.ocupado} cerrar={() => setDialogo(null)}
          enviar={(obs) => void acc.ejecutar(() => api.post(`${base}/${dialogo.p.id}/retirar`, { observacion: obs, versionEsperada: dialogo.p.version }))} />
      )}
    </div>
  );
}

function FilaPrevisto({ p, editable, accion }: { p: PrevistoOt; editable: boolean; accion: (t: 'modificar' | 'sustituir' | 'retirar') => void }): ReactNode {
  const real = Number(p.consumidaValidadaStock);
  const hayReal = real > 0 || Number(p.consumidaPendienteStock) > 0;
  return (
    <li className="fila-previsto" aria-label={`${p.articulo.codigo} · ${p.articulo.descripcion}`}>
      <div>
        <strong>{p.articulo.descripcion}</strong> <span className="mono pequena">{p.articulo.codigo}</span>
        {p.sustituyeA !== null && <span className="etiqueta info">Sustituye a {p.sustituyeA.descripcion}</span>}
        {p.incidencia !== null && <span className="etiqueta error">{INCIDENCIA[p.incidencia]}</span>}
        <div className="cifras-previsto pequena">
          <span>Previsto: <strong>{p.cantidad} {p.unidad.codigo}</strong>{p.previstaStock !== null && p.unidad.codigo !== p.unidadStock && <span className="suave"> (= {p.previstaStock} {p.unidadStock})</span>}</span>
          <span>Real: <strong>{p.consumidaValidadaStock} {p.unidadStock}</strong>{Number(p.consumidaPendienteStock) > 0 && <span className="suave"> · pendiente {p.consumidaPendienteStock} {p.unidadStock}</span>}</span>
          {p.desviacionStock !== null && hayReal && (
            <span className={`desviacion ${Number(p.desviacionStock) > 0 ? 'por-encima' : Number(p.desviacionStock) < 0 ? 'por-debajo' : ''}`}>
              Desviación: <strong>{desviacionTexto(p.desviacionStock, p.unidadStock)}</strong></span>
          )}
        </div>
        {p.entregadaStock != null && Number(p.entregadaStock) > 0 && (
          <div className="pequena suave">Entregado: {p.entregadaStock} {p.unidadStock}{Number(p.devueltaStock ?? '0') > 0 && ` · devuelto ${p.devueltaStock ?? ''} ${p.unidadStock}`}</div>
        )}
        {(p.entregasSustitutas ?? []).length > 0 && (
          <div className="pequena" role="note">
            {(p.entregasSustitutas ?? []).map((s) => <span key={s.idArticulo} className="etiqueta info">Previsto {p.articulo.descripcion} → entregado {s.descripcion} · {s.entregadaStock} {s.unidadStock}</span>)}
          </div>
        )}
        {p.sustituciones.length > 0 && (
          <div className="sustitucion-real pequena" role="note">
            <span className="etiqueta info">Sustituido en ejecución</span>{' '}
            {p.sustituciones.map((s) => <span key={s.idArticulo}>Real: {s.descripcion} · {s.validadaStock} {s.unidadStock}{Number(s.pendienteStock) > 0 && ` (pendiente ${s.pendienteStock})`} </span>)}
          </div>
        )}
        {p.instrucciones !== null && <div className="suave pequena">{p.instrucciones}</div>}
        {p.stockInferior && <div className="nota aviso pequena" role="note">{TEXTO_STOCK_INFERIOR} <span className="suave">(disponible en el centro: {p.disponibleCentro} {p.unidadStock})</span></div>}
      </div>
      {editable && (
        <span className="fila acciones-previsto">
          <button type="button" className="pequeno" disabled={!p.articulo.activo} onClick={() => accion('modificar')}>Modificar</button>
          <button type="button" className="pequeno" onClick={() => accion('sustituir')}>Sustituir</button>
          <button type="button" className="pequeno" onClick={() => accion('retirar')}>Retirar</button>
        </span>
      )}
    </li>
  );
}

function DialogoRetirar({ p, error, ocupado, cerrar, enviar }: { p: PrevistoOt; error: unknown; ocupado: boolean; cerrar: () => void; enviar: (obs: string) => void }): ReactNode {
  const [obs, setObs] = useState('');
  return (
    <Modal titulo="Retirar material previsto" cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Volver</button><button type="button" className="principal" disabled={ocupado} onClick={() => enviar(obs.trim())}>Retirar</button></>}>
      {error !== null && <ErrorCaja error={error} />}
      <p><strong>{p.articulo.descripcion}</strong> · {p.cantidad} {p.unidad.codigo}</p>
      <p className="suave pequena">Deja de estar previsto en esta OT; queda en el histórico. Los consumos ya enlazados se conservan.</p>
      <div className="campo"><label htmlFor="ret-obs">Motivo (opcional)</label>
        <input id="ret-obs" maxLength={1000} value={obs} onChange={(ev) => setObs(ev.target.value)} /></div>
    </Modal>
  );
}
